import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkOrganizationExists, storeOrganizationData } from '@/services/authentication/organizationService';

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  orgName: string;
  orgId: string;
  orgDescription: string;
}

export const useCreateOrganization = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    orgName: '',
    orgId: '',
    orgDescription: ''
  });
  const [error, setError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    if (name === 'confirmPassword' || name === 'password') {
      if (name === 'confirmPassword' && value !== formData.password) {
        setPasswordError('Passwords do not match');
      } else if (name === 'password' && value !== formData.confirmPassword) {
        setPasswordError('Passwords do not match');
      } else {
        setPasswordError('');
      }
    } else if (name === 'orgId') {
      // Validate organization ID format (alphanumeric with hyphens and underscores, 3-50 characters)
      const orgIdRegex = /^[a-zA-Z0-9-_]{3,50}$/;
      if (!orgIdRegex.test(value)) {
        setError('Organization ID must be 3-50 characters long and can only contain letters, numbers, hyphens, and underscores');
      }
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || 
        !formData.orgName || !formData.orgId || !formData.orgDescription) {
      setError('Please fill in all fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Password complexity validation
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }

    // Validate organization ID format
    const orgIdRegex = /^[a-zA-Z0-9-_]{3,50}$/;
    if (!orgIdRegex.test(formData.orgId)) {
      setError('Organization ID must be 3-50 characters long and can only contain letters, numbers, hyphens, and underscores');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const tenantExists = await checkOrganizationExists(formData.orgId);
      
      if (tenantExists) {
        setError('This organization ID is already taken. Please choose a different one.');
        return;
      }

      storeOrganizationData({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        orgId: formData.orgId,
        orgName: formData.orgName,
        orgDescription: formData.orgDescription
      });
      
      navigate(`/organization/onboarding`);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Oops! Something went wrong.");
      }
    }
  };

  return {
    formData,
    error,
    passwordError,
    handleChange,
    handleSubmit
  };
}; 